import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import {
  CYRILLIC_REGEXP,
  getBankNameRegExp,
  getBicRegExp
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidateBankDetailsModel from 'components/NewPaymentFormV2/models/ValidateBankDetailsModel';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './BankDetailsStep.scss';
import { PAYMENT_METHOD } from '../../../../../common/constants';

const BankDetailsStep = ({ newPaymentStore, customerNumber, userAccounts, userStore }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      bankName: newPaymentStore.bankDetailsStep?.bankName || '',
      bankCountry: newPaymentStore.bankDetailsStep?.bankCountry || '',
      bic: newPaymentStore.bankDetailsStep?.bic || '',
      paymentPurpose: newPaymentStore.bankDetailsStep?.paymentPurpose || ''
    },
    validationSchema: Yup.object().shape({
      bankName: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(60, i18n.getMessage('client.validation.message.maxSize', { size: 60 }))
        .matches(getBankNameRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
          })
        }),
      bankCountry: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      bic: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(11, i18n.getMessage('client.validation.message.maxSize', { size: 11 }))
        .min(8, i18n.getMessage('client.validation.message.minSize', { size: 8 }))
        .matches(getBicRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyUppercaseLatinLettersAndDigits')
        }),
      paymentPurpose: Yup.string().when('bankCountry', (bankCountry, schema) => {
        if (bankCountry === 'AE') {
          return schema.trim().required(i18n.getMessage('client.validation.message.isEmpty'));
        }
        return schema;
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidateBankDetailsModel({ ...newPaymentStore.bankDetailsStep, ...values });

      newPaymentStore.updateBankDetailsStep(values);

      await newPaymentStore.validateBankDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleBankCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  // Payment purpose options for UAE
  const paymentPurposeOptions = userStore?.paymentPurposes
    ? Object.entries(userStore.paymentPurposes).map(([key, value]) => ({
        key: key,
        value: `${key} - ${value}`
      }))
    : [];

  const handlePaymentPurposeChange = (name, data) => handleChange({ target: { name, value: data } });

  // Check if UAE is selected
  const isUAESelected = values.bankCountry === 'AE';

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-bank-details-step'}>
      <div className={'new-payment-bank-details-step-inputs-wrapper'}>
        <Input
          className={'new-payment-bank-details-step-input'}
          name={'bic'}
          label={i18n.getMessage('templates.form.bic.label')}
          placeholder={i18n.getMessage('templates.form.bic.placeholder')}
          value={values.bic}
          error={errors.bic || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bic')}
          onChange={handleChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsStep?.bic
            )
          }
          isRequired
        />
        <Input
          className={'new-payment-bank-details-step-input'}
          name={'bankName'}
          label={i18n.getMessage('templates.form.bankName.label')}
          placeholder={i18n.getMessage('templates.form.bankName.placeholder')}
          value={values.bankName}
          error={errors.bankName || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bankName')}
          onChange={handleChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsStep?.bankName
            )
          }
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'new-payment-bank-details-step-input'}
          name={'bankCountry'}
          label={i18n.getMessage('templates.form.bankCountry.label')}
          placeholder={i18n.getMessage('templates.form.bankCountry.placeholder')}
          options={countriesOptions}
          value={values.bankCountry}
          error={errors.bankCountry || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bankCountry')}
          onChange={handleBankCountryChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsStep?.bankCountry
            )
          }
          isRequired
        />
        {isUAESelected && (
          <InputDropDownSearch
            autoComplete={'off'}
            className={'new-payment-bank-details-step-input'}
            name={'paymentPurpose'}
            label={i18n.getMessage('templates.form.paymentPurpose.label')}
            placeholder={i18n.getMessage('templates.form.paymentPurpose.placeholder')}
            options={paymentPurposeOptions}
            value={values.paymentPurpose}
            error={
              errors.paymentPurpose || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'paymentPurpose')
            }
            onChange={handlePaymentPurposeChange}
            isRequired
          />
        )}
      </div>
      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-bank-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

BankDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore,
  userStore: stores.userStore
}))(observer(BankDetailsStep));
